/* eslint-disable react-hooks/exhaustive-deps */
import queryString from 'querystring';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useHistory, useRouteMatch } from 'react-router-dom';
import debounce from 'debounce';
import { useIsMounted } from './useIsMounted';

const useInitialRenderEffect = () => {
  const initialRender = useRef(true);

  useEffect(() => {
    initialRender.current = false;
  }, []);

  return initialRender.current;
};

const useMongoSearch = ({
  serviceArgs = [],
  fetchObjects,
  searchArgs = {},
  searchType,
  showArchived = false,
  sourceTypes = null,
  setFollowingFiltered = null,
}) => {
  let debouncedFn;

  const history = useHistory();
  const { url } = useRouteMatch();
  const { search, pathname: currentURL } = useLocation();
  const isMounted = useIsMounted();

  const query = queryString.parse(search.slice(1));
  const [loading, setLoading] = useState(null);
  const [totalDocs, setTotalDocs] = useState(0);
  const [links, setLinks] = useState(null);
  const [objects, setObjects] = useState([]);

  const [autoCompleteTerms, setAutoCompleteTerms] = useState('');

  const processResponse = ({ data, totalDocs: td, links: l }) => {
    if (isMounted()) {
      setObjects(data);
      setTotalDocs(td);
      setLinks(l);
      setLoading(false);
    }
  };

  const fetchData = async ({ searchParams = search } = {}) => {
    const params = new URLSearchParams(searchParams);
    if (showArchived) params.set('showArchived', 'true');
    return fetchObjects(...serviceArgs, `?${params.toString()}`);
  };

  const refreshObjects = () => {
    if (isMounted()) {
      setLoading(true);
      fetchData().then(processResponse);
    }
  };

  const parseStatsForDownload = ({ stats, columns }) => {
    return stats.map((stat) => {
      return Object.keys(columns)
        .slice(1)
        .map((column) => {
          const { field } = columns[column];
          return { v: stat[field || columns[column]], ...columns[column].downloadOpts };
        });
    });
  };

  const onDownloadClick = async ({ columns, filename, downloadXlsx }) => {
    const params = new URLSearchParams(search);
    params.set('page[size]', '0');
    params.set('page[after]', '1');

    const stats = await fetchData({ searchParams: `?${params.toString()}` });
    const statsForDownload = parseStatsForDownload({ stats: stats.data, columns });
    const headers = Object.keys(columns)
      .slice(1)
      .map((column) => {
        return column === 'title' ? 'Name' : column;
      });

    downloadXlsx({ data: [{ name: 'Summary', data: [headers, ...statsForDownload] }], filename });
  };

  const handleChange = (event, value) => {
    if (value === null) return;
    const params = new URLSearchParams(search);
    params.set('page[after]', value);
    history.push({ search: `?${decodeURI(params.toString())}` });
  };

  const activePage = currentURL === url;

  useEffect(() => {
    refreshObjects();
  }, [JSON.stringify(serviceArgs), query['page[after]']]);

  const initialRender = useInitialRenderEffect();

  useEffect(() => {
    if (activePage) {
      if (!initialRender) {
        handleChange(null, 1);
        refreshObjects();
      }
    }
  }, [query.sort]);

  const onSearchClear = () => {
    setAutoCompleteTerms('');
  };

  const onSearchChange = (event) => {
    if (event && event.persist) event.persist();

    const {
      target: { value },
    } = event;
    setAutoCompleteTerms(value);

    if (!debouncedFn) {
      debouncedFn = debounce(({ terms }) => {
        if (terms === '') {
          return;
        }
        fetchObjects(...serviceArgs, `?query=${terms}`).then((res) => {
          processResponse(res);
        });
      }, 100);
    }

    debouncedFn({
      terms: value,
      navigate: search,
      type: searchType,
      searchServiceArgs: searchArgs,
      scopedUrl: url,
      useArchived: showArchived,
      scopedSourceTypes: sourceTypes,
      scopedSetFollowingFiltered: setFollowingFiltered,
    });
  };

  useEffect(() => {
    if (!initialRender) {
      if (autoCompleteTerms) {
        onSearchChange({ target: { value: autoCompleteTerms } });
      } else {
        refreshObjects();
      }
    }
  }, [showArchived, autoCompleteTerms]);

  const handleDialogClose = (event, reason, opts = {}) => {
    const { url: customUrl } = opts;
    const returnUrl = customUrl || url;
    if (['backdropClick', 'cancel'].includes(reason)) {
      history.push(`${returnUrl}${search}`);
      return;
    }

    refreshObjects();

    const { search: customSearch, wizard } = opts;

    if (!wizard) {
      history.push(`${returnUrl}${customSearch !== undefined ? customSearch : search}`);
    }
  };

  return {
    totalDocs,
    links,
    objects,
    handleDialogClose,
    handleChange,
    onSearchChange,
    onSearchClear,
    autoCompleteTerms,
    setAutoCompleteTerms,
    loading,
    onDownloadClick,
    refreshObjects,
  };
};

export default useMongoSearch;
